/**
 * MonitorPage.jsx — Activity log with:
 *  - All action types grouped by category in the filter dropdown
 *  - Clear logs panel: clear before a date, after a date, or clear all
 *  - Print button
 */

import { useEffect, useState } from "react";
import { adminGetLogs } from "../../services/api";

// ── All action types with badge colours, grouped by category ──────────────────
const ACTION_META = {
  // Auth
  login:                    { bg:"#EAF3DE", color:"#27500A", group:"Auth"      },
  login_failed:             { bg:"#FCEBEB", color:"#A32D2D", group:"Auth"      },
  register:                 { bg:"#E6F1FB", color:"#0C447C", group:"Auth"      },
  change_password:          { bg:"#f1f3f5", color:"#495057", group:"Auth"      },
  forgot_password:          { bg:"#f1f3f5", color:"#495057", group:"Auth"      },
  password_reset_complete:  { bg:"#EAF3DE", color:"#27500A", group:"Auth"      },
  // Search & documents (public)
  search:                   { bg:"#EEEDFE", color:"#3C3489", group:"Public"    },
  download:                 { bg:"#FAEEDA", color:"#633806", group:"Public"    },
  // Scheme management
  upload:                   { bg:"#FAEEDA", color:"#633806", group:"Schemes"   },
  score_updated:            { bg:"#EAF3DE", color:"#27500A", group:"Schemes"   },
  evaluation_updated:       { bg:"#EAF3DE", color:"#27500A", group:"Schemes"   },
  scheme_deleted:           { bg:"#FCEBEB", color:"#A32D2D", group:"Schemes"   },
  // Documents
  doc_attached:             { bg:"#FAEEDA", color:"#633806", group:"Documents" },
  doc_deleted:              { bg:"#FCEBEB", color:"#A32D2D", group:"Documents" },
  doc_reassigned:           { bg:"#f1f3f5", color:"#495057", group:"Documents" },
  admin_doc_deleted:        { bg:"#FCEBEB", color:"#A32D2D", group:"Documents" },
  // User management
  admin_user_active:        { bg:"#EAF3DE", color:"#27500A", group:"Users"     },
  admin_user_blocked:       { bg:"#FCEBEB", color:"#A32D2D", group:"Users"     },
  admin_user_pending:       { bg:"#FAEEDA", color:"#633806", group:"Users"     },
  admin_user_deleted:       { bg:"#FCEBEB", color:"#A32D2D", group:"Users"     },
  admin_password_reset:     { bg:"#FAEEDA", color:"#633806", group:"Users"     },
  admin_role_changed:       { bg:"#EEEDFE", color:"#3C3489", group:"Users"     },
  admin_profile_edited:     { bg:"#f1f3f5", color:"#495057", group:"Users"     },
  admin_privileges_updated: { bg:"#EEEDFE", color:"#3C3489", group:"Users"     },
  // System
  logs_cleared:             { bg:"#FCEBEB", color:"#A32D2D", group:"System"    },
};

const GROUPS = ["Auth", "Public", "Schemes", "Documents", "Users", "System"];

const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day:"2-digit", month:"short", year:"numeric",
    hour:"2-digit", minute:"2-digit", second:"2-digit",
  });
}

// ── Clear logs panel ──────────────────────────────────────────────────────────

function ClearLogsPanel({ onCleared }) {
  const [open,      setOpen]      = useState(false);
  const [mode,      setMode]      = useState("all");  // "all" | "before" | "after"
  const [dateVal,   setDateVal]   = useState("");
  const [clearing,  setClearing]  = useState(false);
  const [result,    setResult]    = useState("");
  const [confirmed, setConfirmed] = useState(false);

  function reset() { setMode("all"); setDateVal(""); setResult(""); setConfirmed(false); }

  async function handleClear() {
    if (!confirmed) { setConfirmed(true); return; }
    setClearing(true); setResult("");
    try {
      const body = {};
      if (mode === "before" && dateVal) body.before_date = dateVal;
      if (mode === "after"  && dateVal) body.after_date  = dateVal;
      const res  = await fetch("/api/admin/logs", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? "Clear failed");
      setResult(`✓ ${data.message}`);
      setConfirmed(false);
      setTimeout(() => { setOpen(false); reset(); onCleared(); }, 1800);
    } catch (e) { setResult(`✕ ${e.message}`); setConfirmed(false); }
    finally { setClearing(false); }
  }

  const needsDate    = mode !== "all";
  const readyToClear = mode === "all" || (needsDate && dateVal);

  return (
    <div style={{ position:"relative" }}>
      <button onClick={() => { setOpen(v => !v); reset(); }} style={{
        display:"inline-flex", alignItems:"center", gap:6,
        background: open ? "#FCEBEB" : "#fff",
        border:"1.5px solid #D63B2F", borderRadius:7,
        padding:"5px 14px", fontSize:13, color:"#A32D2D",
        cursor:"pointer", fontWeight:600, fontFamily:"'Nunito',sans-serif",
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
        </svg>
        Clear logs
      </button>

      {open && (
        <div className="clear-panel" style={{
          position:"absolute", top:"calc(100% + 6px)", right:0,
          background:"#fff", border:"1.5px solid #D63B2F",
          borderRadius:10, padding:"16px 18px", width:320,
          boxShadow:"0 4px 20px rgba(0,0,0,.14)", zIndex:200,
        }}>
          {/* Title */}
          <div style={{ fontSize:13, fontWeight:700, color:"#A32D2D",
            marginBottom:12, display:"flex", alignItems:"center", gap:6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Clear activity logs
          </div>

          {/* Mode selection */}
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
            {[
              { val:"all",    label:"Clear ALL logs (no date filter)" },
              { val:"before", label:"Clear logs created BEFORE a date" },
              { val:"after",  label:"Clear logs created AFTER a date"  },
            ].map(({ val, label }) => (
              <label key={val} style={{ display:"flex", alignItems:"center",
                gap:8, fontSize:13, cursor:"pointer", color:"#212529" }}>
                <input type="radio" name="clearMode" value={val}
                  checked={mode === val}
                  onChange={() => { setMode(val); setDateVal(""); setConfirmed(false); setResult(""); }}
                  style={{ accentColor:"#D63B2F" }}/>
                {label}
              </label>
            ))}
          </div>

          {/* Date picker */}
          {needsDate && (
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11, fontWeight:600, color:"#495057",
                display:"block", marginBottom:4 }}>
                Select date
              </label>
              <input type="date" value={dateVal}
                onChange={e => { setDateVal(e.target.value); setConfirmed(false); }}
                style={{ width:"100%", padding:"6px 10px", fontSize:13,
                  border:"1.5px solid #dee2e6", borderRadius:7, color:"#212529",
                  fontFamily:"inherit" }}/>
            </div>
          )}

          {/* Result */}
          {result && (
            <div style={{ fontSize:12, padding:"7px 10px", borderRadius:6, marginBottom:10,
              background: result.startsWith("✓") ? "#EAF3DE" : "#FCEBEB",
              color: result.startsWith("✓") ? "#27500A" : "#A32D2D" }}>
              {result}
            </div>
          )}

          {/* Confirm step */}
          {readyToClear && !result && confirmed && (
            <div style={{ fontSize:12, padding:"8px 10px", borderRadius:6,
              background:"#FAEEDA", color:"#633806", marginBottom:10 }}>
              ⚠ This cannot be undone. Click "Confirm delete" to proceed.
            </div>
          )}

          {/* Action buttons */}
          {readyToClear && !result && (
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={handleClear} disabled={clearing}
                style={{ flex:1, padding:"8px 0", fontSize:13, fontWeight:700,
                  background: confirmed ? "#D63B2F" : "#fff",
                  color: confirmed ? "#fff" : "#D63B2F",
                  border:"1.5px solid #D63B2F", borderRadius:7,
                  cursor:"pointer", fontFamily:"'Nunito',sans-serif",
                  transition:"all .15s" }}>
                {clearing ? "Clearing…" : confirmed ? "Confirm delete" : "Clear now"}
              </button>
              <button onClick={() => { setOpen(false); reset(); }}
                style={{ padding:"8px 14px", fontSize:13, background:"#f8f9fa",
                  color:"#6c757d", border:"1px solid #dee2e6",
                  borderRadius:7, cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          )}
          {!readyToClear && (
            <p style={{ fontSize:11, color:"#adb5bd", margin:0 }}>
              Select a date above to continue.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main monitor page ─────────────────────────────────────────────────────────

export default function MonitorPage() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [filter,  setFilter]  = useState("");
  const [limit,   setLimit]   = useState(100);

  useEffect(() => { load(); }, [filter, limit]);

  async function load() {
    setLoading(true); setError("");
    try   { setLogs(await adminGetLogs(limit, filter)); }
    catch (e) { setError(e.message); }
    finally   { setLoading(false); }
  }

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #monitor-printable, #monitor-printable * { visibility: visible; }
          #monitor-printable { position: absolute; top: 0; left: 0; width: 100%; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
        }
        .print-only { display: none; }

        /* ── Responsive toolbar ── */
        @media (max-width: 640px) {
          .monitor-toolbar { flex-direction: column; align-items: stretch !important; }
          .monitor-controls { width: 100%; }
          .monitor-controls select {
            flex: 1 1 calc(50% - 4px);
            min-width: 130px;
            height: 36px !important;
          }
          .monitor-controls > button {
            flex: 1 1 calc(50% - 4px);
            min-width: 130px;
            height: 36px !important;
            justify-content: center;
          }
          .monitor-controls > div {
            flex: 1 1 calc(50% - 4px);
            min-width: 130px;
          }
          .monitor-controls > div > button {
            width: 100%;
            height: 36px !important;
          }
          /* Pin the clear-panel inside the viewport on small screens */
          .clear-panel {
            position: fixed !important;
            top: 80px !important;
            left: 16px !important;
            right: 16px !important;
            width: auto !important;
            max-width: none !important;
          }
        }
      `}</style>

      <div className="p-4" id="monitor-printable">

        {/* ── Toolbar ── */}
        <div className="no-print monitor-toolbar" style={{ display:"flex", alignItems:"flex-start",
          justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:20 }}>
          <div>
            <h2 style={{ fontSize:18, fontWeight:700, margin:0, color:"#5C4B00",
              fontFamily:"'Nunito',sans-serif" }}>Activity monitor</h2>
            <div style={{ fontSize:12, color:"#775F46", marginTop:2 }}>
              {logs.length} record{logs.length !== 1 ? "s" : ""} displayed
            </div>
          </div>

          <div className="monitor-controls" style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            {/* Action filter — grouped dropdown */}
            <select style={{ fontSize:12, padding:"5px 10px", border:"1px solid #d4dbc8",
              borderRadius:7, color:"#5C4B00", background:"#fff", height:32 }}
              value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="">All actions</option>
              {GROUPS.map(group => {
                const actions = Object.entries(ACTION_META)
                  .filter(([, m]) => m.group === group);
                if (!actions.length) return null;
                return (
                  <optgroup key={group} label={`── ${group}`}>
                    {actions.map(([action]) => (
                      <option key={action} value={action}>
                        {action.replace(/_/g, " ")}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>

            {/* Row limit */}
            <select style={{ fontSize:12, padding:"5px 10px", border:"1px solid #d4dbc8",
              borderRadius:7, color:"#5C4B00", background:"#fff", height:32 }}
              value={limit} onChange={e => setLimit(Number(e.target.value))}>
              {[50, 100, 200, 500].map(n => (
                <option key={n} value={n}>Last {n}</option>
              ))}
            </select>

            <button onClick={load} disabled={loading}
              style={{ height:32, padding:"0 14px", fontSize:12, borderRadius:7,
                border:"1px solid #d4dbc8", background:"#fff",
                color:"#5C4B00", cursor:"pointer", fontFamily:"'Nunito',sans-serif" }}>
              Refresh
            </button>

            <button onClick={() => window.print()}
              style={{ height:32, padding:"0 14px", fontSize:12, borderRadius:7,
                border:"none", background:"#5C4B00", color:"#fff",
                cursor:"pointer", fontFamily:"'Nunito',sans-serif",
                display:"inline-flex", alignItems:"center", gap:5 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print logs
            </button>

            <ClearLogsPanel onCleared={load} />
          </div>
        </div>

        {/* Print header */}
        <div className="print-only" style={{ marginBottom:"1rem" }}>
          <h2 style={{ fontSize:16, marginBottom:4 }}>Activity log — BeXyl IPM DSS</h2>
          <p style={{ fontSize:12, color:"#6c757d" }}>
            Printed: {new Date().toLocaleString("en-GB")}
            {filter ? ` · Filter: ${filter.replace(/_/g, " ")}` : " · All actions"}
            {" · "}{logs.length} records
          </p>
        </div>

        {error   && <div style={{ background:"#FCEBEB",color:"#A32D2D",padding:"10px 14px",
          borderRadius:8, fontSize:13, marginBottom:14 }}>{error}</div>}
        {loading && <div style={{ color:"#adb5bd", fontSize:13 }}>Loading…</div>}

        {!loading && logs.length === 0 && (
          <div style={{ textAlign:"center", color:"#adb5bd", fontSize:13,
            border:"1px dashed #dee2e6", background:"#fff",
            borderRadius:10, padding:"48px 0" }}>
            No logs {filter ? `for "${filter.replace(/_/g," ")}"` : "yet"}.
          </div>
        )}

        {!loading && logs.length > 0 && (
          <div style={{ background:"#fff", border:"1px solid #dee2e6",
            borderRadius:12, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", minWidth:780, borderCollapse:"collapse", fontSize:12 }}>
                <thead style={{ background:"#f8f9fa" }}>
                  <tr>
                    {[
                      { label:"Time",   style:{ width:155, whiteSpace:"nowrap" } },
                      { label:"Action", style:{ width:170, whiteSpace:"nowrap" } },
                      { label:"User",   style:{ width:150, whiteSpace:"nowrap" } },
                      { label:"Detail", style:{ minWidth:200 } },
                      { label:"IP",     style:{ width:120, whiteSpace:"nowrap" }, cls:"no-print" },
                    ].map(({ label, style: s, cls }) => (
                      <th key={label} className={cls || ""}
                        style={{ padding:"8px 12px", fontWeight:600, color:"#6c757d",
                          textAlign:"left", borderBottom:"1px solid #dee2e6", ...s }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => {
                    const meta = ACTION_META[log.action] ?? { bg:"#f1f3f5", color:"#495057" };
                    let detail = "";
                    try {
                      const d = JSON.parse(log.detail ?? "{}");
                      detail  = Object.entries(d)
                        .map(([k,v]) => `${k.replace(/_/g," ")}: ${JSON.stringify(v)}`)
                        .join(" · ");
                    } catch { detail = log.detail ?? ""; }

                    return (
                      <tr key={log.id} style={{
                        borderBottom:"1px solid #f1f3f5",
                        background: idx % 2 === 0 ? "#fff" : "#fafcf9",
                      }}>
                        <td style={{ padding:"7px 12px", color:"#6c757d",
                          whiteSpace:"nowrap", verticalAlign:"top" }}>
                          {fmtTime(log.created_at)}
                        </td>
                        <td style={{ padding:"7px 12px", verticalAlign:"top" }}>
                          <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px",
                            borderRadius:4, background:meta.bg, color:meta.color,
                            whiteSpace:"nowrap", display:"inline-block" }}>
                            {log.action.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td style={{ padding:"7px 12px", color:"#495057",
                          whiteSpace:"nowrap", verticalAlign:"top" }}>
                          {log.user_email ?? "—"}
                        </td>
                        <td style={{ padding:"7px 12px", color:"#6c757d",
                          wordBreak:"break-word", whiteSpace:"pre-wrap",
                          lineHeight:1.5, verticalAlign:"top", minWidth:200, maxWidth:380 }}>
                          {detail}
                        </td>
                        <td className="no-print" style={{ padding:"7px 12px",
                          color:"#adb5bd", whiteSpace:"nowrap", verticalAlign:"top" }}>
                          {log.ip ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
